import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from "sonner";
import {
  FiUsers, FiChevronLeft, FiChevronRight, FiPlus, FiTrash2, FiEdit2, FiCopy,
  FiCalendar, FiCheckCircle, FiXCircle, FiClock, FiBarChart2, FiUser, FiArrowRight, FiMoreHorizontal
} from 'react-icons/fi';
import {
  getStudentsByCoach, getWeekStartDate, getWeekDays,
  COACH_SUBJECTS, COACH_SUBJECTS_LGS, SUBJECT_COLORS,
  createTask, deleteTask, updateTask, copyWeekTasks, clearDayTasks, clearWeekTasks,
  getExamColor, saveDayNoteWithWeek, getDayNotesForWeek,
  getAllTasksForStudent, getAllDayNotesForStudent
} from '../../utils/coachUtils';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MiniCalendar from '../../components/MiniCalendar';
import '../../styles/CoachPanel.css';

// ── Sortable Task Component ──
function SortableTaskCard({ task, onEdit, onDelete, onViewTask }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, data: { ...task } });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    '--task-color': SUBJECT_COLORS[task.subject] || '#64748b'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`coach-task-card coach-task-card--${task.status}`}
    >
      <div className="coach-task-color-bar" />
      <div 
        className="coach-task-body" 
        style={{ paddingRight: '22px', display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'center', cursor: 'pointer' }}
        onClick={(e) => {
          if (onViewTask) {
            e.stopPropagation();
            onViewTask(task);
          }
        }}
        title="Detayları gör"
      >
        <div style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', lineHeight: 1.3 }}>
          {task.startTime && <span style={{ color: '#4f46e5', marginRight: '4px' }}>{task.startTime}</span>}
          {task.category} - {task.subject} - {task.title}
        </div>
        {(task.description || task.duration) && (
          <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 600 }}>
            {task.duration && <span style={{ color: '#10b981', marginRight: '4px' }}>⏱ {task.duration}dk</span>}
            {task.description ? (task.description.length > 20 ? task.description.slice(0, 20) + '...' : task.description) : ''}
          </div>
        )}
      </div>
      {task.status !== 'pending' && (
        <div className={`coach-task-status-badge coach-task-status-badge--${task.status}`}>
          {task.status === 'completed' ? '✓' : '✗'}
        </div>
      )}
      <div className="coach-task-actions-group" style={{ display: 'flex', gap: '2px', position: 'absolute', top: '4px', right: '4px', bottom: 'auto', flexDirection: 'row' }}>
        <button className="coach-task-delete" onPointerDown={e => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onEdit(task); }} title="Düzenle">
          <FiEdit2 size={10} style={{ color: '#818cf8' }} />
        </button>
        <button className="coach-task-delete" onPointerDown={e => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onDelete(task.id); }} title="Sil">
          <FiTrash2 size={10} style={{ color: '#ef4444' }} />
        </button>
      </div>
      
      <div style={{ position: 'absolute', top: '6px', right: '6px', color: '#94a3b8', opacity: 0.6 }}>
        <FiMoreHorizontal size={14} />
      </div>
    </div>
  );
}

// ── Droppable Day Column Component ──
import { useDroppable } from '@dnd-kit/core';
function DroppableDayColumn({ id, children, className }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={className}
      style={{
        backgroundColor: isOver ? 'rgba(99, 102, 241, 0.05)' : undefined,
        transition: 'background-color 0.2s ease',
        minHeight: '200px'
      }}
    >
      {children}
    </div>
  );
}

export default function CoachDashboardPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [weekStart, setWeekStart] = useState(getWeekStartDate());
  const [weekTasks, setWeekTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copyingWeek, setCopyingWeek] = useState(false);

  // Task modal (Unified for Add & Edit)
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskDate, setTaskDate] = useState('');
  const [taskCategory, setTaskCategory] = useState('TYT');
  const [taskSubject, setTaskSubject] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskStartTime, setTaskStartTime] = useState('09:00');
  const [taskDuration, setTaskDuration] = useState(60);

  const [viewTask, setViewTask] = useState(null);

  // Day Notes state
  const [weekNotes, setWeekNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNoteDate, setEditingNoteDate] = useState(null);
  const [dayNoteText, setDayNoteText] = useState('');

  // All student data for MiniCalendar
  const [allStudentTasks, setAllStudentTasks] = useState([]);
  const [allStudentNotes, setAllStudentNotes] = useState([]);

  const fetchStudents = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await getStudentsByCoach(userId);
      setStudents(data);
      if (data.length > 0 && !selectedStudent) {
        setSelectedStudent(data[0]);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedStudent]);

  const fetchWeekTasks = useCallback(async () => {
    if (!selectedStudent) return;
    try {
      const q = query(
        collection(db, 'coachTasks'),
        where('studentId', '==', selectedStudent.id),
        where('weekStartDate', '==', weekStart)
      );
      const snapshot = await getDocs(q);
      const tasks = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      tasks.sort((a, b) => a.date.localeCompare(b.date) || (a.order || 0) - (b.order || 0));
      setWeekTasks(tasks);
      
      const notes = await getDayNotesForWeek(selectedStudent.id, weekStart);
      setWeekNotes(notes);
    } catch (err) {
      console.error('Error fetching tasks/notes:', err);
      setWeekTasks([]);
      setWeekNotes([]);
    }
  }, [selectedStudent, weekStart]);

  const fetchAllStudentData = useCallback(async () => {
    if (!selectedStudent) return;
    try {
      const t = await getAllTasksForStudent(selectedStudent.id);
      const n = await getAllDayNotesForStudent(selectedStudent.id);
      setAllStudentTasks(t);
      setAllStudentNotes(n);
    } catch (err) {
      console.error('Error fetching all data:', err);
    }
  }, [selectedStudent]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);
  useEffect(() => { fetchWeekTasks(); }, [fetchWeekTasks]);
  useEffect(() => { fetchAllStudentData(); }, [fetchAllStudentData]);

  const weekDays = getWeekDays(weekStart);
  const today = new Date().toISOString().split('T')[0];

  // Week stats
  const totalWeekTasks = weekTasks.length;
  const completedWeekTasks = weekTasks.filter(t => t.status === 'completed').length;
  const skippedWeekTasks = weekTasks.filter(t => t.status === 'skipped').length;
  const pendingWeekTasks = totalWeekTasks - completedWeekTasks - skippedWeekTasks;
  const weekCompletionRate = totalWeekTasks > 0 ? Math.round((completedWeekTasks / totalWeekTasks) * 100) : 0;

  // Format week range label
  const weekEndDate = new Date(weekStart);
  weekEndDate.setDate(weekEndDate.getDate() + 6);
  const formatDate = (d) => {
    const date = new Date(d);
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    return `${date.getDate()} ${months[date.getMonth()]}`;
  };
  const weekLabel = `${formatDate(weekStart)} – ${formatDate(weekEndDate)}`;

  const navigateWeek = (direction) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + direction * 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const openAddTask = (date) => {
    const activeSubjects = selectedStudent?.examType === 'LGS' ? COACH_SUBJECTS_LGS : COACH_SUBJECTS;
    const defaultCat = Object.keys(activeSubjects)[0];
    
    setEditingTask(null);
    setTaskDate(date);
    setTaskCategory(defaultCat);
    setTaskSubject(activeSubjects[defaultCat][0]);
    setTaskTitle('');
    setTaskDescription('');
    setTaskStartTime('09:00');
    setTaskDuration(60);
    setShowTaskModal(true);
  };

  const openEditTask = (task) => {
    const activeSubjects = selectedStudent?.examType === 'LGS' ? COACH_SUBJECTS_LGS : COACH_SUBJECTS;
    const defaultCat = Object.keys(activeSubjects)[0];

    setEditingTask(task);
    setTaskDate(task.date);
    setTaskCategory(task.category || defaultCat);
    setTaskSubject(task.subject);
    setTaskTitle(task.title);
    setTaskDescription(task.description || '');
    setTaskStartTime(task.startTime || '09:00');
    setTaskDuration(task.duration || 60);
    setShowTaskModal(true);
  };

  const handleSaveTask = async () => {
    if (!taskTitle.trim()) { toast.error('Görev başlığı gerekli'); return; }
    try {
      if (editingTask) {
        // Edit mode
        await updateTask(editingTask.id, {
          title: taskTitle,
          subject: taskSubject,
          category: taskCategory,
          description: taskDescription || null,
          startTime: taskStartTime,
          duration: Number(taskDuration),
        });
        toast.success('Görev güncellendi ✓');
      } else {
        // Add mode
        const dayTasks = weekTasks.filter(t => t.date === taskDate);
        await createTask({
          coachId: userId, studentId: selectedStudent.id,
          date: taskDate, weekStartDate: weekStart,
          title: taskTitle, subject: taskSubject, category: taskCategory,
          description: taskDescription || null, order: dayTasks.length,
          startTime: taskStartTime, duration: Number(taskDuration),
        });
        toast.success('Görev eklendi ✓');
      }
      setShowTaskModal(false);
      fetchWeekTasks();
    } catch (err) {
      console.error(err);
      toast.error('Görev kaydedilemedi');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    try {
      await deleteTask(taskId);
      toast.success('Silindi ✓');
      fetchWeekTasks();
    } catch {
      toast.error('Silinemedi');
    }
  };

  const handleSaveDayNote = async () => {
    try {
      await saveDayNoteWithWeek(selectedStudent.id, editingNoteDate, weekStart, dayNoteText);
      toast.success('Not kaydedildi ✓');
      setShowNoteModal(false);
      fetchWeekTasks();
    } catch (err) {
      toast.error('Not kaydedilemedi');
    }
  };

  const handleClearWeek = async () => {
    if (!selectedStudent) return;
    if (!window.confirm('Bu haftadaki TÜM görevleri silmek istediğinize emin misiniz? Bu işlem geri alınamaz!')) return;
    try {
      await clearWeekTasks(selectedStudent.id, weekStart);
      toast.success('Hafta temizlendi ✓');
      fetchWeekTasks();
    } catch {
      toast.error('Hafta temizlenemedi');
    }
  };

  const handleClearDay = async (date) => {
    if (!selectedStudent) return;
    if (!window.confirm('Bu gündeki tüm görevleri silmek istediğinize emin misiniz?')) return;
    try {
      await clearDayTasks(selectedStudent.id, date);
      toast.success('Gün temizlendi ✓');
      fetchWeekTasks();
    } catch {
      toast.error('Gün temizlenemedi');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeId, setActiveId] = useState(null);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    // Is it dropped on another task or on a day column directly?
    const activeTask = weekTasks.find(t => t.id === activeId);
    if (!activeTask) return;

    let targetDate = activeTask.date;
    let newOrder = activeTask.order;

    // Check if over is a day column
    const overDayCol = String(overId).startsWith('day-col-') ? String(overId).replace('day-col-', '') : null;
    
    if (overDayCol) {
      targetDate = overDayCol;
      const tasksInTargetDay = weekTasks.filter(t => t.date === targetDate);
      newOrder = tasksInTargetDay.length;
    } else {
      const overTask = weekTasks.find(t => t.id === overId);
      if (overTask) {
        targetDate = overTask.date;
        newOrder = overTask.order; // this is simplified, we'll just insert it
      }
    }

    if (activeTask.date === targetDate && activeTask.id === overId) return;

    // Optimistic UI update
    setWeekTasks(prev => {
      return prev.map(t => {
        if (t.id === activeId) {
          return { ...t, date: targetDate, order: newOrder };
        }
        return t;
      });
    });

    try {
      await updateTask(activeId, { date: targetDate, order: newOrder });
    } catch (err) {
      toast.error('Sıralama kaydedilemedi');
      fetchWeekTasks(); // rollback
    }
  };

  const handleCopyPreviousWeek = async () => {
    if (!selectedStudent) return;
    const prevWeekDateObj = new Date(weekStart);
    prevWeekDateObj.setDate(prevWeekDateObj.getDate() - 7);
    const prevWeekStartStr = prevWeekDateObj.toISOString().split('T')[0];

    setCopyingWeek(true);
    try {
      const copiedCount = await copyWeekTasks(
        selectedStudent.id,
        userId,
        prevWeekStartStr,
        weekStart
      );
      if (copiedCount > 0) {
        toast.success(`${copiedCount} görev geçen haftadan kopyalandı! 🎉`);
        fetchWeekTasks();
      } else {
        toast.info('Geçen haftada kopyalanacak görev bulunamadı.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Görevler kopyalanamadı');
    } finally {
      setCopyingWeek(false);
    }
  };

  if (loading) {
    return (
      <div className="coach-page">
        <div className="coach-loading">
          <div className="coach-loading-spinner" />
          <span>Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-page">
      {/* Hero Header */}
      <motion.div className="coach-hero" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="coach-hero-icon">🎓</div>
        <h1 className="coach-hero-title">Koç <span>Paneli</span></h1>
        <p className="coach-hero-subtitle">{students.length} aktif öğrenci · {totalWeekTasks} görev planlandı</p>
      </motion.div>

      <div className="coach-layout">
        {/* ── Sidebar ── */}
        <motion.aside className="coach-sidebar" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="coach-sidebar-header">
            <FiUsers size={14} />
            <span>Öğrencilerim</span>
            <span className="coach-sidebar-badge">{students.length}</span>
          </div>
          <div className="coach-student-list">
            {students.length === 0 ? (
              <div className="coach-student-empty">
                <FiUser size={24} />
                <p>Henüz öğrenciniz yok</p>
                <span>Admin panelinden atanmasını bekleyin</span>
              </div>
            ) : (
              students.map((student, i) => {
                const isSelected = selectedStudent?.id === student.id;
                return (
                  <motion.div
                    key={student.id}
                    className={`coach-student-card ${isSelected ? 'coach-student-card--active' : ''}`}
                    onClick={() => setSelectedStudent(student)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div 
                      className="coach-student-avatar" 
                      data-initial={(student.username || '?')[0].toUpperCase()}
                      style={{ background: getExamColor(student.examType) }}
                    >
                      {(student.username || '?')[0].toUpperCase()}
                    </div>
                    <div className="coach-student-info">
                      <div className="coach-student-name">{student.username || 'İsimsiz'}</div>
                      <div className="coach-student-meta">{student.examType || 'Belirtilmemiş'}</div>
                    </div>
                    {isSelected && <div className="coach-student-indicator" />}
                  </motion.div>
                );
              })
            )}
          </div>
          
          {/* Mini Calendar in Sidebar */}
          {selectedStudent && (
            <div style={{ padding: '16px 12px' }}>
              <MiniCalendar 
                tasks={allStudentTasks}
                dayNotes={allStudentNotes}
                selectedDate={weekStart}
                onDayClick={(date) => {
                  setWeekStart(getWeekStartDate(new Date(date)));
                }}
              />
            </div>
          )}
        </motion.aside>

        {/* ── Main Content ── */}
        <motion.main className="coach-main" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
          {!selectedStudent ? (
            <div className="coach-empty-state">
              <FiUsers size={48} />
              <h3>Öğrenci Seçin</h3>
              <p>Sol panelden bir öğrenci seçerek haftalık programını yönetin</p>
            </div>
          ) : (
            <>
              {/* Student Info Bar */}
              <div className="coach-student-bar">
                <div className="coach-student-bar-left">
                  <div 
                    className="coach-student-bar-avatar"
                    style={{ background: getExamColor(selectedStudent.examType) }}
                  >
                    {(selectedStudent.username || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <h2 className="coach-student-bar-name">{selectedStudent.username}</h2>
                    <span className="coach-student-bar-exam">{selectedStudent.examType}</span>
                  </div>
                </div>
                <div className="coach-student-bar-actions">
                  <button className="coach-btn coach-btn--outline" onClick={() => navigate(`/coach/student/${selectedStudent.id}`)}>
                    <FiBarChart2 size={14} />
                    <span>Detay & Notlar</span>
                    <FiArrowRight size={12} />
                  </button>
                </div>
              </div>

              {/* Week Stats */}
              {totalWeekTasks > 0 && (
                <motion.div className="coach-week-stats" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <div className="coach-week-stat">
                    <div className="coach-week-stat-icon" style={{ color: '#10b981', background: 'rgba(16,185,129,0.1)' }}>
                      <FiCheckCircle size={16} />
                    </div>
                    <div className="coach-week-stat-value">{completedWeekTasks}</div>
                    <div className="coach-week-stat-label">Tamamlanan</div>
                  </div>
                  <div className="coach-week-stat">
                    <div className="coach-week-stat-icon" style={{ color: '#818cf8', background: 'rgba(99,102,241,0.1)' }}>
                      <FiClock size={16} />
                    </div>
                    <div className="coach-week-stat-value">{pendingWeekTasks}</div>
                    <div className="coach-week-stat-label">Bekleyen</div>
                  </div>
                  <div className="coach-week-stat">
                    <div className="coach-week-stat-icon" style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)' }}>
                      <FiXCircle size={16} />
                    </div>
                    <div className="coach-week-stat-value">{skippedWeekTasks}</div>
                    <div className="coach-week-stat-label">Atlanan</div>
                  </div>
                  <div className="coach-week-stat coach-week-stat--rate">
                    <div className="coach-week-stat-ring">
                      <svg viewBox="0 0 36 36" className="coach-ring-svg">
                        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
                        <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#10b981" strokeWidth="3" strokeDasharray={`${weekCompletionRate}, 100`} strokeLinecap="round" />
                      </svg>
                      <span className="coach-ring-text">{weekCompletionRate}%</span>
                    </div>
                    <div className="coach-week-stat-label">Haftalık Oran</div>
                  </div>
                </motion.div>
              )}

              {/* Week Navigation & Copy Actions */}
              <div className="coach-week-nav-bar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="coach-nav-btn" onClick={() => navigateWeek(-1)}>
                    <FiChevronLeft size={18} />
                  </button>
                  <div className="coach-week-nav-center">
                    <FiCalendar size={14} />
                    <span>{weekLabel}</span>
                  </div>
                  <button className="coach-nav-btn" onClick={() => navigateWeek(1)}>
                    <FiChevronRight size={18} />
                  </button>
                  <button className="coach-nav-btn coach-nav-btn--today" onClick={() => setWeekStart(getWeekStartDate())}>
                    Bugün
                  </button>
                  
                  {/* Dropdown Placeholder */}
                  <select className="coach-form-select" style={{ width: '160px', marginLeft: '12px', padding: '8px 12px', height: '36px', fontSize: '12px', borderRadius: '10px' }} disabled>
                    <option>Tümünü Görüntüle</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                  {/* Copy previous week schedule trigger */}
                  <button
                    className="coach-nav-btn"
                    style={{
                      width: 'auto',
                      padding: '0 12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      gap: '6px',
                      background: 'rgba(79, 70, 229, 0.08)',
                      borderColor: 'rgba(79, 70, 229, 0.25)',
                      color: '#4f46e5',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onClick={handleCopyPreviousWeek}
                    disabled={copyingWeek}
                    title="Geçen haftanın programını kopyala"
                  >
                    <FiCopy size={13} />
                    <span>{copyingWeek ? 'Kopyalanıyor...' : 'Önceki Haftayı Kopyala'}</span>
                  </button>

                  <button
                    className="coach-nav-btn"
                    style={{
                      width: 'auto',
                      padding: '0 16px',
                      fontSize: '12px',
                      fontWeight: 700,
                      gap: '6px',
                      background: '#fee2e2',
                      borderColor: '#fca5a5',
                      color: '#dc2626',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '10px'
                    }}
                    onClick={handleClearWeek}
                    title="Haftayı Temizle"
                  >
                    <FiTrash2 size={14} />
                    <span>Haftayı Temizle</span>
                  </button>
                </div>
              </div>

              {/* Weekly Grid */}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <div className="coach-week-grid">
                  {weekDays.map((day, dayIdx) => {
                    const dayTasks = weekTasks.filter(t => t.date === day.date);
                    const isToday = day.date === today;
                    const totalDuration = dayTasks.reduce((acc, t) => acc + (Number(t.duration) || 0), 0);
                    const hours = Math.floor(totalDuration / 60);
                    const mins = totalDuration % 60;
                    const durationStr = hours > 0 ? `${hours} sa ${mins > 0 ? mins + ' dk' : ''}` : `${mins} dk`;

                    return (
                      <motion.div
                        key={day.date}
                        className={`coach-day-col ${isToday ? 'coach-day-col--today' : ''}`}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: dayIdx * 0.04 }}
                      >
                        <div className={`coach-day-header ${isToday ? 'coach-day-header--today' : ''}`} style={{ position: 'relative' }}>
                          <div className="coach-day-name">{day.dayName}</div>
                          <div className="coach-day-number">{day.dayNumber}</div>
                          
                          <button 
                            onClick={() => {
                              const dayStr = day.date;
                              const existingObj = weekNotes.find(n => n.date === dayStr);
                              setEditingNoteDate(dayStr);
                              setDayNoteText(existingObj ? existingObj.note : '');
                              setShowNoteModal(true);
                            }}
                            title="Günün anlamını / Not ekle"
                            style={{ position: 'absolute', top: '8px', right: '8px', background: 'transparent', border: 'none', color: weekNotes.find(n => n.date === day.date)?.note ? '#4f46e5' : '#94a3b8', cursor: 'pointer', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <FiEdit2 size={12} />
                          </button>

                          {weekNotes.find(n => n.date === day.date)?.note && (
                            <div style={{ marginTop: '8px', padding: '6px', background: 'rgba(79,70,229,0.1)', borderRadius: '6px', fontSize: '10px', color: '#4f46e5', fontWeight: 700, fontStyle: 'italic', wordBreak: 'break-word', border: '1px solid rgba(79,70,229,0.2)' }}>
                              📌 {weekNotes.find(n => n.date === day.date).note}
                            </div>
                          )}
                          <div style={{ fontSize: '10px', color: '#64748b', marginTop: '6px', fontWeight: 600 }}>
                            Toplam Görev: {dayTasks.length}
                          </div>
                          {totalDuration > 0 && (
                            <div style={{ fontSize: '10px', color: '#10b981', marginTop: '2px', fontWeight: 700 }}>
                              ⏱ {durationStr}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '8px 0' }}>
                          <button 
                            style={{
                              width: '100%', padding: '8px', borderRadius: '10px',
                              border: '1px dashed #c7d2fe', background: '#eef2ff',
                              color: '#4f46e5', fontSize: '11px', fontWeight: 700,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                            }}
                            onClick={() => openAddTask(day.date)}
                          >
                            <FiPlus size={12} /> Görev Ekle
                          </button>
                          <button 
                            style={{
                              width: '100%', padding: '8px', borderRadius: '10px',
                              border: '1px dashed #fca5a5', background: '#fef2f2',
                              color: '#dc2626', fontSize: '11px', fontWeight: 700,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                            }}
                            onClick={() => handleClearDay(day.date)}
                          >
                            <FiTrash2 size={12} /> Günü Temizle
                          </button>
                        </div>

                        <DroppableDayColumn id={`day-col-${day.date}`} className="coach-day-tasks">
                          <SortableContext items={dayTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                            <AnimatePresence>
                              {dayTasks.map((task) => (
                                <SortableTaskCard 
                                  key={task.id} 
                                  task={task} 
                                  onEdit={openEditTask} 
                                  onDelete={handleDeleteTask} 
                                  onViewTask={setViewTask}
                                />
                              ))}
                            </AnimatePresence>
                          </SortableContext>
                        </DroppableDayColumn>
                      </motion.div>
                    );
                  })}
                </div>
                
                {/* Drag Overlay for smooth dragging preview */}
                <DragOverlay>
                  {activeId ? (
                    <SortableTaskCard 
                      task={weekTasks.find(t => t.id === activeId)} 
                      onEdit={() => {}} 
                      onDelete={() => {}} 
                      onViewTask={() => {}}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </>
          )}
        </motion.main>
      </div>

      {/* ── Add / Edit Task Modal ── */}
      <AnimatePresence>
        {showTaskModal && (
          <motion.div className="coach-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowTaskModal(false)}>
            <motion.div
              className="coach-modal"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="coach-modal-header">
                <div>
                  <h3>{editingTask ? 'Görevi Düzenle' : 'Görev Ekle'}</h3>
                  <span className="coach-modal-date">{taskDate}</span>
                </div>
                <button className="coach-modal-close" onClick={() => setShowTaskModal(false)}>✕</button>
              </div>
              <div className="coach-modal-body">
                <div className="coach-form-row">
                  <div className="coach-form-group">
                    <label className="coach-form-label">Kategori</label>
                    <select className="coach-form-select" value={taskCategory} onChange={e => { 
                      const activeSubjects = selectedStudent?.examType === 'LGS' ? COACH_SUBJECTS_LGS : COACH_SUBJECTS;
                      setTaskCategory(e.target.value); 
                      setTaskSubject(activeSubjects[e.target.value][0]); 
                    }}>
                      {Object.keys(selectedStudent?.examType === 'LGS' ? COACH_SUBJECTS_LGS : COACH_SUBJECTS).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                  <div className="coach-form-group">
                    <label className="coach-form-label">Ders</label>
                    <select className="coach-form-select" value={taskSubject} onChange={e => setTaskSubject(e.target.value)}>
                      {(selectedStudent?.examType === 'LGS' ? COACH_SUBJECTS_LGS : COACH_SUBJECTS)[taskCategory]?.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                    </select>
                  </div>
                </div>

                <div className="coach-form-group">
                  <label className="coach-form-label">Görev Başlığı</label>
                  <input className="coach-form-input" type="text" placeholder="Örn: Konu Anlatımı - İntegral" value={taskTitle} onChange={e => setTaskTitle(e.target.value)} autoFocus />
                </div>

                <div className="coach-form-row">
                  <div className="coach-form-group">
                    <label className="coach-form-label">Başlangıç Saati</label>
                    <input className="coach-form-input" type="time" value={taskStartTime} onChange={e => setTaskStartTime(e.target.value)} />
                  </div>
                  <div className="coach-form-group">
                    <label className="coach-form-label">Tahmini Süre</label>
                    <select className="coach-form-select" value={taskDuration} onChange={e => setTaskDuration(e.target.value)}>
                      <option value="15">15 dk</option>
                      <option value="30">30 dk</option>
                      <option value="45">45 dk</option>
                      <option value="60">60 dk</option>
                      <option value="90">90 dk</option>
                      <option value="120">120 dk</option>
                      <option value="180">180 dk</option>
                    </select>
                  </div>
                </div>

                <div className="coach-form-group">
                  <label className="coach-form-label">Açıklama <span style={{ opacity: 0.5 }}>(isteğe bağlı)</span></label>
                  <textarea className="coach-form-textarea" placeholder="Kaynak, sayfa, detay..." value={taskDescription} onChange={e => setTaskDescription(e.target.value)} />
                </div>

                {/* Subject Color Preview */}
                <div className="coach-form-preview">
                  <div className="coach-form-preview-dot" style={{ background: SUBJECT_COLORS[taskSubject] || '#64748b' }} />
                  <span>{taskCategory} — {taskSubject}</span>
                </div>

                <button className="coach-form-submit" onClick={handleSaveTask}>
                  <FiPlus size={16} />
                  {editingTask ? 'Değişiklikleri Kaydet' : 'Görev Ekle'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── View Task Modal ── */}
      <AnimatePresence>
        {viewTask && (
          <motion.div 
            className="coach-modal-overlay" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            onClick={() => setViewTask(null)}
          >
            <motion.div
              className="coach-modal"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: '400px',
                padding: '24px',
                textAlign: 'left'
              }}
            >
              <button 
                onClick={() => setViewTask(null)}
                style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '20px' }}
              >✕</button>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: SUBJECT_COLORS[viewTask.subject] || '#64748b' }} />
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>{viewTask.category} • {viewTask.subject}</span>
              </div>
              
              <h3 style={{ fontSize: '20px', fontWeight: 800, color: '#1e293b', marginBottom: '16px', lineHeight: 1.3 }}>
                {viewTask.title}
              </h3>
              
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                {viewTask.startTime && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(79,70,229,0.1)', padding: '6px 12px', borderRadius: '8px', color: '#4f46e5', fontSize: '13px', fontWeight: 700 }}>
                    <FiClock size={14} /> {viewTask.startTime}
                  </div>
                )}
                {viewTask.duration && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(16,185,129,0.1)', padding: '6px 12px', borderRadius: '8px', color: '#10b981', fontSize: '13px', fontWeight: 700 }}>
                    ⏱ {viewTask.duration} dk
                  </div>
                )}
              </div>
              
              {viewTask.description ? (
                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>Açıklama</div>
                  <div style={{ fontSize: '14px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {viewTask.description}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '14px', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: '10px 0' }}>
                  Açıklama eklenmemiş.
                </div>
              )}
              
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => {
                    const taskToEdit = viewTask;
                    setViewTask(null);
                    openEditTask(taskToEdit);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '10px', background: 'rgba(79,70,229,0.1)', color: '#4f46e5', fontWeight: 700, border: 'none', cursor: 'pointer' }}
                >
                  <FiEdit2 size={14} /> Düzenle
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Day Note Modal ── */}
      <AnimatePresence>
        {showNoteModal && (
          <motion.div className="coach-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowNoteModal(false)}>
            <motion.div
              className="coach-modal"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="coach-modal-header">
                <div>
                  <h3>Günün Notu</h3>
                  <span className="coach-modal-date">{editingNoteDate}</span>
                </div>
                <button className="coach-modal-close" onClick={() => setShowNoteModal(false)}>✕</button>
              </div>
              <div className="coach-modal-body">
                <div className="coach-form-group">
                  <label className="coach-form-label">Not (Örn: TYT Türkiye Geneli, Matematik Yazılısı)</label>
                  <input 
                    type="text" 
                    className="coach-form-input" 
                    placeholder="Günün anlamı veya notu..." 
                    value={dayNoteText} 
                    onChange={e => setDayNoteText(e.target.value)} 
                    autoFocus 
                  />
                  <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Notu silmek için kutuyu boş bırakıp kaydedin. Öğrenci de bu notu görebilir.</span>
                </div>
                <button className="coach-form-submit" onClick={handleSaveDayNote}>
                  Kaydet
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
